import { Input, OnInit, ViewChild } from '@angular/core';
import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
    channelStatePropertyMap, INotification, INotificationEdit, INotificationRecipient,
    NotificationChannel, notificationChannels, NotificationChannelState
} from '../../../models/Notification';
import { AppService } from '../../../services/app.service';
import { NotificationService } from '../../../services/notification.service';
import { ITableColumn } from '../../../shared/table/table.component';

interface IRecipientRow {
    recipient: INotificationRecipient;
    searchName: string;
}

@Component({
    selector: 'app-notification-edit',
    templateUrl: './notification-edit.component.html'
})
export class NotificationEditComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: NotificationService
    ) { }

    @Input() data: INotification;

    model: INotificationEdit = <INotificationEdit>{};
    recipientRows: IRecipientRow[] = [];
    isFormSubmitted: boolean;

    readonly allChannels = notificationChannels;
    readonly availableChannels: { [key: string]: boolean } = {};
    readonly requiredChannels: { [key: string]: boolean } = {};

    readonly options: {
        channel: { value: NotificationChannel, text: string }[],
        requestStatus: { value: boolean, text: string }[]
    } = {
            channel: [],
            requestStatus: [
                { text: this.app.translate('notification_requestApproved'), value: true },
                { text: this.app.translate('notification_requestRejected'), value: false }
            ]
        };

    readonly maxLengths = {
        name: 50,
        sender: 200,
        subject: 500,
        message: 4000,
        shortMessage: 1000
    };

    readonly textEditorConfig = {
        convert_urls: false,
        forced_root_block: '',
        entity_encoding: 'raw',
        plugins: 'link textcolor table code lists',
        toolbar:
            'undo redo | formatselect | bold italic forecolor backcolor'
            + ' | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat'
            + ' | table link | code'
    };

    readonly recipientColumns: ITableColumn[] = [
        { label: 'notification_lblRecipient' },
        { label: 'notification_lblLanguage' },
        { label: 'notification_channelMyRsu' },
        { label: 'notification_channelRsuEmail' },
        { label: 'notification_channelSms' }
    ];

    readonly recipientFilterValues: {
        search: string,
        channels: { [name: string]: boolean }
    } = {
            search: '',
            channels: notificationChannels.reduce((all, t) => {
                all[t] = undefined;
                return all;
            }, {})
        };

    get isShortContentRequired() {
        return this.model.SmsChannel >= NotificationChannelState.Available;
    }

    get isFullContentRequired() {
        return this.model.MyRsuChannel >= NotificationChannelState.Available || this.model.RsuEmailChannel >= NotificationChannelState.Available;
    }

    get validations() {
        const values = this.model;

        const shortRequired = this.isShortContentRequired;
        const fullRequired = this.isFullContentRequired;

        return {
            sender: {
                required: this.checkRequired(values.Sender),
                maxlength: this.checkMaxLength(values.Sender, this.maxLengths.sender)
            },
            subjectLv: {
                required: fullRequired && this.checkRequired(values.SubjectLV),
                maxlength: this.checkMaxLength(values.SubjectLV, this.maxLengths.subject)
            },
            subjectEn: {
                required: fullRequired && this.checkRequired(values.SubjectEN),
                maxlength: this.checkMaxLength(values.SubjectEN, this.maxLengths.subject)
            },
            messageLv: {
                required: fullRequired && this.checkRequired(values.MessageLV),
                maxlength: this.checkMaxLength(values.MessageLV, this.maxLengths.message)
            },
            messageEn: {
                required: fullRequired && this.checkRequired(values.MessageEN),
                maxlength: this.checkMaxLength(values.MessageEN, this.maxLengths.message)
            },
            shortMessageLv: {
                required: shortRequired && this.checkRequired(values.ShortMessageLV),
                maxlength: this.checkMaxLength(values.ShortMessageLV, this.maxLengths.shortMessage)
            },
            shortMessageEn: {
                required: shortRequired && this.checkRequired(values.ShortMessageEN),
                maxlength: this.checkMaxLength(values.ShortMessageEN, this.maxLengths.shortMessage)
            },
            availableChannels: {
                min: Object.values(this.availableChannels).filter(t => t).length == 0 && this.availableChannelRequiredError
            }
        };
    }

    get stats() {
        return notificationChannels.map(c => {
            let approved = 0;
            let rejected = 0;

            let isAvailable = this.availableChannels[c];

            this.data.Recipients.forEach(r => {
                if (this.isRecipientChannelActive(r, c)) {
                    approved++;
                } else if (isAvailable) {
                    rejected++;
                }
            });

            return {
                channel: c,
                approved,
                rejected
            };
        });
    }

    readonly requiredError = this.app.translate('fieldIsRequired');
    readonly namePatternError = this.app.translate('notificationTemplate_hintName');
    readonly availableChannelRequiredError = this.app.translate('notificationTemplate_availableChannelRequired');

    private readonly recipientSearchSubj = new Subject<string>();
    private allRecipientRows: IRecipientRow[] = [];

    @ViewChild('form', { static: false }) private form: NgForm;

    ngOnInit() {
        if (!this.data) return;

        this.options.channel = notificationChannels.map(t => {
            return {
                value: t,
                text: this.app.translate(`notification_channel${t}`)
            };
        });

        this.recipientSearchSubj.pipe(debounceTime(300), distinctUntilChanged()).subscribe(term => {
            this.filterRecipients();
        });

        this.fillModel();
    }

    onAvailableChannelChange(channel: NotificationChannel, checked: boolean) {
        this.availableChannels[channel] = checked;

        if (checked) {
            this.model[channelStatePropertyMap[channel]] = NotificationChannelState.Available;
        } else {
            this.requiredChannels[channel] = false;
            this.recipientFilterValues.channels[channel] = undefined;
        }
    }

    onRequiredChannelChange(channel: NotificationChannel, checked: boolean) {
        if (checked) {
            this.availableChannels[channel] = true;
            this.model[channelStatePropertyMap[channel]] = NotificationChannelState.Required;
        } else {
            this.model[channelStatePropertyMap[channel]] = NotificationChannelState.Available;
        }
    }

    send() {
        this.isFormSubmitted = true;
        if (!this.validate()) return;

        this.app.confirm({
            text: this.app.translate('notification_confirmSend'),
            okText: this.app.translate('notification_btnSend'),
            title: this.app.translate('notification_sendTitle')
        }, ok => {
            if (ok) {
                this.app.addLoading(this.service.send(this.data.Id, this.model)).subscribe(() => {
                    this.app.notify(this.app.translate('notification_submitted'));
                    this.app.navigateByUrl('/notifications');
                });
            }
        });
    }

    getMaxLengthError(maxLength: number): string {
        return this.app.translate('fieldError_lengthMinMax').replace('{0}', 0).replace('{1}', maxLength);
    }

    getErrors(validation: { [key: string]: string }): string[] {
        return validation ? Object.values(validation).filter(t => !!t) : [];
    }

    isChannelAvailable(channel: NotificationChannel): boolean {
        return this.availableChannels[channel];
    }

    isRecipientChannelActive(recipient: INotificationRecipient, channel: NotificationChannel): boolean {
        if (this.requiredChannels[channel]) return true;
        if (this.availableChannels[channel] && recipient.Channels.includes(channel)) return true;

        return false;
    }

    searchRecipients(term: string) {
        this.recipientSearchSubj.next(term);
    }

    filterRecipients() {
        const search = (this.recipientFilterValues.search || '').toLowerCase();
        const hasChannelFilter = Object.keys(this.recipientFilterValues.channels).filter(t => this.recipientFilterValues.channels[t] !== undefined).length > 0;

        let rows: IRecipientRow[] = [];

        if (!search && !hasChannelFilter) {
            rows = this.allRecipientRows;
        } else {
            rows = this.allRecipientRows.filter(t => {
                if (hasChannelFilter) {
                    for (let i = 0; i < notificationChannels.length; i++) {
                        const ch = notificationChannels[i];
                        const chFilter = this.recipientFilterValues.channels[ch];

                        if (this.availableChannels[ch] && chFilter !== undefined) {
                            if (chFilter != this.isRecipientChannelActive(t.recipient, ch)) return false;
                        }
                    }
                }

                if (search && !t.searchName.includes(search)) return false;

                return true;
            });
        }

        this.recipientRows = [...rows];
    }

    private fillModel() {
        const data = this.data;

        this.model.MessageEN = data.MessageEN;
        this.model.MessageLV = data.MessageLV;
        this.model.Sender = data.Sender;
        this.model.ShortMessageEN = data.ShortMessageEN;
        this.model.ShortMessageLV = data.ShortMessageLV;
        this.model.SubjectEN = data.SubjectEN;
        this.model.SubjectLV = data.SubjectLV;

        notificationChannels.forEach(t => {
            const state = this.data[channelStatePropertyMap[t]];

            this.model[channelStatePropertyMap[t]] = state;

            this.availableChannels[t] = state >= NotificationChannelState.Available;
            this.requiredChannels[t] = state == NotificationChannelState.Required;
        });

        this.recipientRows = data.Recipients.map(t => {
            return {
                recipient: t,
                searchName: `${t.Name} ${t.Surname} ${t.Upn}`.toLowerCase()
            };
        });

        this.allRecipientRows = this.recipientRows;
    }

    private validate() {
        if (!this.form.valid || Object.values(this.validations).some(t => Object.values(t).includes(true))) {
            this.app.alert.error(this.app.translate('formError'));
            return false;
        }

        return true;
    }

    private checkRequired(value: any): string {
        return (value === undefined || value === null || value === '') ? this.requiredError : undefined;
    }

    private checkMaxLength(value: string, maxLength: number): string {
        return value && value.length > maxLength ? this.getMaxLengthError(maxLength) : undefined;
    }
}
